import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Bookmark, ExternalLink, Sparkles, Send, Flag, FolderPlus, Folder,
  Star, Clock, TrendingUp, Target, Plus, X, Lightbulb, Check,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const COST_STYLE = {
  Free: "bg-emerald-500/15 text-emerald-300", Paid: "bg-amber-500/15 text-amber-300",
  Subscription: "bg-blue-500/15 text-blue-300", "Government Fee": "bg-purple-500/15 text-purple-300",
};

function CostBadge({ cost }) {
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${COST_STYLE[cost] || "bg-white/8 text-[#A1A1AA]"}`}>{cost}</span>;
}

function ResourceCard({ r, onOpen, onBookmark }) {
  return (
    <div data-testid={`resource-card-${r.resource_id}`} className="fv-card-hover rounded-2xl border border-white/8 bg-[#121214] p-5 flex flex-col">
      <div className="flex items-start justify-between mb-2">
        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{r.category}</span>
        <button onClick={() => onBookmark(r)} data-testid={`resource-bookmark-${r.resource_id}`} className={r.bookmarked ? "text-[#D4AF37]" : "text-[#52525B] hover:text-white"}>
          <Bookmark className="w-4 h-4" fill={r.bookmarked ? "currentColor" : "none"} />
        </button>
      </div>
      <h3 className="font-heading text-lg text-white mb-1">{r.name}</h3>
      <p className="text-sm text-[#71717A] leading-relaxed flex-1 line-clamp-2 mb-3">{r.description}</p>
      <div className="flex items-center gap-2 mb-3">
        <CostBadge cost={r.cost} />
        {r.state !== "All" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA]">{r.state}</span>}
      </div>
      <button onClick={() => onOpen(r)} data-testid={`resource-open-${r.resource_id}`} className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1 self-start">View details <ExternalLink className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function ResourceDetail({ resource, onClose, onBookmark, collections, onAddToCollection, onVisit }) {
  const [aiMsgs, setAiMsgs] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const ask = async (preset) => {
    const msg = preset || aiInput.trim();
    if (!msg) return;
    setAiInput(""); setAiMsgs((m) => [...m, { role: "user", content: msg }]); setAiLoading(true);
    try {
      const { data } = await api.post("/resource-center/ai", { message: msg, resource_id: resource.resource_id });
      setAiMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI unavailable"); }
    finally { setAiLoading(false); }
  };
  const report = async () => {
    await api.post(`/resource-center/resources/${resource.resource_id}/report`, { note: "Reported as outdated" });
    toast.success("Thanks — we'll review this resource.");
  };
  const isExternal = resource.url.startsWith("http");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="resource-detail" className="bg-[#0d0d0f] border-white/10 text-white max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <span className="text-[10px] uppercase tracking-wider text-[#D4AF37]">{resource.category}</span>
          <DialogTitle className="font-heading text-2xl">{resource.name}</DialogTitle>
          <DialogDescription className="sr-only">Resource details, AI advice, and actions</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <CostBadge cost={resource.cost} />
          {resource.state !== "All" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA]">{resource.state}</span>}
          {resource.industry !== "All" && <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/8 text-[#A1A1AA]">{resource.industry}</span>}
        </div>
        <p className="text-sm text-[#D4D4D8] leading-relaxed">{resource.description}</p>
        {resource.ai_summary && (
          <div className="rounded-xl border border-[#D4AF37]/25 bg-[#151310] p-3 flex gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37] shrink-0 mt-0.5" />
            <p className="text-sm text-[#E4E4E7]">{resource.ai_summary}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 text-xs text-[#A1A1AA]">
          <div><span className="text-[#71717A]">Eligibility:</span> {resource.eligibility}</div>
          {resource.contact && <div><span className="text-[#71717A]">Contact:</span> {resource.contact}</div>}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onVisit(resource)} data-testid="resource-visit-btn" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">
            {isExternal ? "Visit website" : "Open in Fairview"} <ExternalLink className="w-4 h-4 ml-1" />
          </Button>
          <Button onClick={() => onBookmark(resource)} data-testid="resource-detail-bookmark" variant="outline" className="border-white/15 bg-transparent text-white rounded-full">
            <Bookmark className="w-4 h-4 mr-1" fill={resource.bookmarked ? "currentColor" : "none"} /> {resource.bookmarked ? "Saved" : "Save"}
          </Button>
          <Button onClick={report} data-testid="resource-report-btn" variant="outline" className="border-white/15 bg-transparent text-[#A1A1AA] rounded-full"><Flag className="w-4 h-4 mr-1" /> Report outdated</Button>
        </div>

        {collections.length > 0 && (
          <div>
            <div className="text-xs text-[#71717A] mb-1.5">Add to a collection:</div>
            <div className="flex flex-wrap gap-1.5">
              {collections.map((c) => (
                <button key={c.collection_id} onClick={() => onAddToCollection(c.collection_id, resource.resource_id)} data-testid={`add-to-collection-${c.collection_id}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-[#A1A1AA] hover:bg-[#D4AF37]/15 hover:text-[#D4AF37] flex items-center gap-1"><Folder className="w-3 h-3" /> {c.name}</button>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-[#121214] p-3">
          <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#D4AF37]" /><span className="text-sm font-medium">Ask the AI Advisor</span></div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {["Why am I seeing this?", "Is this right for my business?", "What should I use first?"].map((p) => (
              <button key={p} onClick={() => ask(p)} className="text-[11px] px-2 py-1 rounded-full border border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/40">{p}</button>
            ))}
          </div>
          <div className="space-y-2 mb-2 max-h-52 overflow-y-auto">
            {aiMsgs.map((m, i) => (
              <div key={i} className={`text-sm rounded-lg px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-[#D4AF37]/15 ml-6" : "bg-[#1C1C1F] mr-6 text-[#E4E4E7]"}`}>{m.content}</div>
            ))}
            {aiLoading && <div className="text-xs text-[#71717A]">Thinking…</div>}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
            <input value={aiInput} onChange={(e) => setAiInput(e.target.value)} data-testid="resource-ai-input" placeholder="Ask about this resource…"
              className="flex-1 bg-[#0d0d0f] border border-white/10 rounded-lg px-3 h-9 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            <Button type="submit" disabled={aiLoading} data-testid="resource-ai-send" className="fv-gold-gradient text-[#0A0A0B] h-9 w-9 p-0 rounded-lg"><Send className="w-4 h-4" /></Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STAGES = ["idea", "formation", "banking", "credit", "funding", "growth", "scale"];
const COSTS = ["Free", "Paid", "Subscription", "Government Fee"];

export default function Resources() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("home");
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ q: "", category: "", stage: "", cost: "", sort: "relevance" });
  const [selected, setSelected] = useState(null);
  const [collections, setCollections] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestForm, setSuggestForm] = useState({ name: "", url: "", category: "Other", note: "" });
  const [newCol, setNewCol] = useState("");

  const loadDash = useCallback(() => api.get("/resource-center/dashboard").then(({ data }) => setDash(data)).catch(() => {}), []);
  const loadCollections = useCallback(() => api.get("/resource-center/collections").then(({ data }) => setCollections(data.collections)).catch(() => {}), []);
  const loadList = useCallback(() => {
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/resource-center/resources?${params}`).then(({ data }) => { setRows(data.resources); setCategories(data.categories); }).catch(() => {});
  }, [filters]);

  useEffect(() => { loadDash(); loadCollections(); }, [loadDash, loadCollections]);
  useEffect(() => { if (tab === "browse") loadList(); }, [tab, loadList]);

  const openResource = async (r) => {
    try { const { data } = await api.get(`/resource-center/resources/${r.resource_id}`); setSelected(data); loadDash(); }
    catch { setSelected(r); }
  };
  const toggleBookmark = async (r) => {
    const val = !r.bookmarked;
    await api.post(`/resource-center/resources/${r.resource_id}/bookmark`, { value: val });
    toast.success(val ? "Saved to your resources" : "Removed");
    setSelected((s) => (s && s.resource_id === r.resource_id ? { ...s, bookmarked: val } : s));
    setRows((rs) => rs.map((x) => (x.resource_id === r.resource_id ? { ...x, bookmarked: val } : x)));
    loadDash();
  };
  const addToCollection = async (cid, rid) => { const { data } = await api.post(`/resource-center/collections/${cid}/items/${rid}`); toast.success(data.in_collection ? "Added to collection" : "Removed from collection"); loadCollections(); };
  const visit = (r) => { if (r.url.startsWith("http")) window.open(r.url, "_blank"); else { setSelected(null); navigate(r.url); } };
  const createCollection = async () => { if (!newCol.trim()) return; await api.post("/resource-center/collections", { name: newCol }); setNewCol(""); toast.success("Collection created"); loadCollections(); };
  const submitSuggestion = async () => {
    if (!suggestForm.name.trim() || !suggestForm.url.trim()) { toast.error("Name and URL required"); return; }
    await api.post("/resource-center/suggest", suggestForm);
    toast.success("Thanks! Your suggestion is pending review.");
    setShowSuggest(false); setSuggestForm({ name: "", url: "", category: "Other", note: "" });
  };

  const Section = ({ icon: Icon, title, items }) => items?.length > 0 && (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3"><Icon className="w-5 h-5 text-[#D4AF37]" /><h2 className="font-heading text-xl text-white">{title}</h2></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => <ResourceCard key={r.resource_id} r={r} onOpen={openResource} onBookmark={toggleBookmark} />)}
      </div>
    </div>
  );

  return (
    <div className="fv-fade-up">
      <PageHeader eyebrow="Grow" title="Resource Center"
        subtitle="A personalized hub of tools, government resources, and organizations — matched to your business, stage, and goals." />

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex gap-1.5">
          {[["home", "For you"], ["browse", "Browse all"], ["collections", "My collections"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`rc-tab-${k}`}
              className={`text-sm px-4 py-2 rounded-full transition-colors ${tab === k ? "fv-gold-gradient text-[#0A0A0B] font-medium" : "bg-white/5 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <Button onClick={() => setShowSuggest(true)} data-testid="rc-suggest-btn" variant="outline" className="border-[#D4AF37]/30 bg-transparent text-[#D4AF37] rounded-full"><Lightbulb className="w-4 h-4 mr-1" /> Suggest a resource</Button>
      </div>

      {tab === "home" && dash && (
        <div data-testid="rc-dashboard">
          <Section icon={Target} title="Recommended for you" items={dash.recommended} />
          <Section icon={Clock} title="Recently viewed" items={dash.recently_viewed} />
          <Section icon={Star} title="Saved resources" items={dash.saved} />
          <Section icon={TrendingUp} title="Trending" items={dash.trending} />
        </div>
      )}

      {tab === "browse" && (
        <div data-testid="rc-browse">
          <div className="flex flex-col lg:flex-row gap-3 mb-5">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="rc-search"
                placeholder="Search resources…" className="w-full bg-[#121214] border border-white/10 rounded-full pl-9 pr-4 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            </div>
            <select value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} data-testid="rc-filter-category" className="bg-[#121214] border border-white/10 rounded-full px-3 h-10 text-sm text-white">
              <option value="">All categories</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))} data-testid="rc-filter-stage" className="bg-[#121214] border border-white/10 rounded-full px-3 h-10 text-sm text-white capitalize">
              <option value="">All stages</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.cost} onChange={(e) => setFilters((f) => ({ ...f, cost: e.target.value }))} data-testid="rc-filter-cost" className="bg-[#121214] border border-white/10 rounded-full px-3 h-10 text-sm text-white">
              <option value="">Any cost</option>{COSTS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filters.sort} onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))} data-testid="rc-sort" className="bg-[#121214] border border-white/10 rounded-full px-3 h-10 text-sm text-white">
              <option value="relevance">Relevance</option><option value="popular">Most popular</option><option value="name">Name</option>
            </select>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rc-results">
            {rows.length === 0 && <p className="text-sm text-[#71717A]">No resources match your filters.</p>}
            {rows.map((r) => <ResourceCard key={r.resource_id} r={r} onOpen={openResource} onBookmark={toggleBookmark} />)}
          </div>
        </div>
      )}

      {tab === "collections" && (
        <div data-testid="rc-collections">
          <div className="flex gap-2 mb-5 max-w-md">
            <input value={newCol} onChange={(e) => setNewCol(e.target.value)} data-testid="rc-new-collection-input" placeholder="New collection name…"
              className="flex-1 bg-[#121214] border border-white/10 rounded-full px-4 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
            <Button onClick={createCollection} data-testid="rc-create-collection" className="fv-gold-gradient text-[#0A0A0B] rounded-full"><FolderPlus className="w-4 h-4 mr-1" /> Create</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.length === 0 && <p className="text-sm text-[#71717A]">No collections yet. Create one and add resources from any card.</p>}
            {collections.map((c) => (
              <div key={c.collection_id} data-testid={`collection-${c.collection_id}`} className="rounded-2xl border border-white/8 bg-[#121214] p-5 flex items-center justify-between">
                <div className="flex items-center gap-2"><Folder className="w-5 h-5 text-[#D4AF37]" /><div><div className="text-white font-medium">{c.name}</div><div className="text-xs text-[#71717A]">{c.count} resources</div></div></div>
                <button onClick={async () => { await api.delete(`/resource-center/collections/${c.collection_id}`); toast.success("Collection deleted"); loadCollections(); }} data-testid={`delete-collection-${c.collection_id}`} className="text-[#52525B] hover:text-red-400"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected && <ResourceDetail resource={selected} onClose={() => setSelected(null)} onBookmark={toggleBookmark} collections={collections} onAddToCollection={addToCollection} onVisit={visit} />}

      <Dialog open={showSuggest} onOpenChange={setShowSuggest}>
        <DialogContent data-testid="rc-suggest-dialog" className="bg-[#0d0d0f] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-heading text-xl">Suggest a resource</DialogTitle><DialogDescription className="sr-only">Submit a resource for admin review</DialogDescription></DialogHeader>
          <input value={suggestForm.name} onChange={(e) => setSuggestForm((f) => ({ ...f, name: e.target.value }))} data-testid="suggest-name" placeholder="Resource name" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" />
          <input value={suggestForm.url} onChange={(e) => setSuggestForm((f) => ({ ...f, url: e.target.value }))} data-testid="suggest-url" placeholder="https://…" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" />
          <select value={suggestForm.category} onChange={(e) => setSuggestForm((f) => ({ ...f, category: e.target.value }))} data-testid="suggest-category" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white">
            <option value="Other">Select category</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <textarea value={suggestForm.note} onChange={(e) => setSuggestForm((f) => ({ ...f, note: e.target.value }))} data-testid="suggest-note" placeholder="Why is this helpful? (optional)" className="bg-[#121214] border border-white/10 rounded-lg p-3 text-sm text-white h-20 resize-none" />
          <Button onClick={submitSuggestion} data-testid="suggest-submit" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Check className="w-4 h-4 mr-1" /> Submit for review</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
